import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import { evictLicenseQueries } from "@/v2-backoffice-client/license/license.utils"
import { LICENSE_SOURCE_OPERATOR } from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseSeatsDialogProps = {
  license: LicenseFragment
  open: boolean
  onOpenChange(open: boolean): void
}

/** The dialog's title and the menu item that opens it say the same thing. */
export function getLicenseSeatsDialogTitle(license: LicenseFragment) {
  return license.LicenseSourceCode === LICENSE_SOURCE_OPERATOR
    ? "Edit Seat Cap"
    : "Override Seat Cap"
}

// Sets the licensed seat cap — in whichever way the row's owner allows:
//
//   * Operator-granted: edits the cap directly (the operator owns it).
//   * Billing-derived: sets the operator OVERRIDE, which wins over the
//     synced quantity and survives every sync run; "Use billing seats"
//     clears it. The dialog shows what billing says so the exception is a
//     conscious one.
//
// Occupied seats next to it come from the live user/invitation counts, so
// the operator sees what the cap is being set against. Opened from the
// license row menu (LicenseSeatsMenuItem) and from the customer panel's
// seat warning (CustomerSeatOveruseNotice). Mount it per open so the field
// re-seeds from the license every time.
function LicenseSeatsDialog(props: LicenseSeatsDialogProps) {
  const { license, open, onOpenChange } = props
  const [seats, setSeats] = useState(
    license.Seats !== null && license.Seats !== undefined
      ? String(license.Seats)
      : "",
  )

  const isOperator = license.LicenseSourceCode === LICENSE_SOURCE_OPERATOR
  const occupied = license.UserCount + license.PendingInvitationCount

  const [updateLicense, { loading, error }] = useLicenseUpdateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      toast({
        variant: "success",
        title: "Seat cap updated",
        description: `${data.updateLicense.CustomerName ?? "The customer"} is now licensed for ${data.updateLicense.Seats ?? "uncapped"} seats.`,
      })
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>{getLicenseSeatsDialogTitle(license)}</Dialog.Title>
          <Dialog.Description>
            {license.CustomerName ?? "This customer"} currently occupies{" "}
            {occupied} {occupied === 1 ? "seat" : "seats"} (active users plus
            open invitations).
            {!isOperator &&
              ` Billing derives ${license.BillingSeats ?? "no cap"} — an override wins over it and survives every sync run; use it for negotiated exceptions, not corrections (fix wrong quantities in Fenerum).`}
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-2">
          <Label htmlFor="licenseSeats">Licensed seats</Label>
          <Input
            id="licenseSeats"
            type="number"
            min={1}
            required
            value={seats}
            onChange={(e) => {
              setSeats(e.target.value)
            }}
          />
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          {!isOperator && license.SeatsOverride != null && (
            <Button
              type="button"
              variant="outline"
              isLoading={loading}
              data-feature-code="UI_BackofficeLicense_Seats_ClearOverride"
              onClick={() => {
                updateLicense({
                  variables: {
                    id: license.Id,
                    input: { ClearSeatsOverride: true },
                  },
                })
              }}
            >
              Use Billing Seats
            </Button>
          )}
          {isOperator && license.Seats != null && (
            <Button
              type="button"
              variant="outline"
              isLoading={loading}
              data-feature-code="UI_BackofficeLicense_Seats_ClearCap"
              onClick={() => {
                updateLicense({
                  variables: {
                    id: license.Id,
                    input: { ClearSeats: true },
                  },
                })
              }}
            >
              Remove Seat Cap
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            disabled={!seats || Number(seats) < 1}
            data-feature-code="UI_BackofficeLicense_Seats_Save"
            onClick={() => {
              updateLicense({
                variables: {
                  id: license.Id,
                  input: isOperator
                    ? { Seats: Number(seats) }
                    : { SeatsOverride: Number(seats) },
                },
              })
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseSeatsDialog
